/**
 * Payroll settlement engine (payroll-settlement spec; design.md "Payroll
 * policy", AD8, AD9).
 *
 * PARITY DUPLICATE — read before editing either copy:
 * This module has an identical twin at `api/_lib/payroll-policy.js`. Both
 * copies MUST produce byte-for-byte identical output for the same input
 * (enforced by `tests/unit/payroll-policy-parity.test.js`). Same Vercel
 * bundler constraint as `js/shared/timesheet-policy.js` /
 * `api/_lib/timesheet-policy.js` — see the "PARITY DUPLICATE" comment there
 * for the full rationale; this file only restates it briefly.
 *
 * Reuses `splitMinutesByDate`/`detectAnomalies` (and their constants) from
 * `./timesheet-policy.js` via re-export — this module does NOT reimplement
 * the midnight-split or anomaly logic. Settlement is GATED by
 * `detectAnomalies`: the caller (api/liquidacion.js, PR 5c) must refuse the
 * run while any anomaly exists in range. `computeLiquidacion` below trusts
 * that gate already ran and does not re-check it, so it stays unit-testable
 * on data that would otherwise fail the gate.
 *
 * SILENT ZERO gotcha (recurring bug class in this change — see AD7 for the
 * Fudo ledgers): a missing amount must never become a `0`, because in
 * payroll a `0` where a real number belongs is a silent OVERPAY
 * (bruto - 0 - 0 = bruto). Every required numeric input is validated
 * explicitly below and THROWS on null/undefined/NaN — never defaulted.
 *
 * Money: integer cents internally, ONE rounding operation per employee per
 * period (AD8), applied at the very end — never per shift, per day, or per
 * date bucket. Minutes stay exact (fractional, unrounded) throughout, same
 * as `splitMinutesByDate` already guarantees.
 */
import {
    ART_OFFSET_MINUTES,
    ANOMALY_MAX_SHIFT_MINUTES,
    splitMinutesByDate,
    detectAnomalies,
} from './timesheet-policy.js';

export { ART_OFFSET_MINUTES, ANOMALY_MAX_SHIFT_MINUTES, splitMinutesByDate, detectAnomalies };

/** Bumped whenever the settlement formula changes; stamped into `liquidaciones.policy_version` (AD9, migration 20). Bumped to @2 for `fijo` + the always-present `modo`/`valor_fijo`/`multiplicador_aplicado` detalle keys (AD1-AD3, migration 22). Bumped to @3 for `otros_descuentos` in the neto formula and in `detalle` (spec liquidacion-otros-descuentos, migration 33). */
export const PAYROLL_POLICY_VERSION = 'payroll-policy@3';

/** Holiday pay multiplier (PRD: double pay on a `feriados` date). The ONLY place this literal lives. */
export const MULTIPLICADOR_FERIADO = 2;

const TIPOS_PAGO_VALIDOS = ['hora', 'dia', 'fijo'];
const FECHA_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Literal `descuento_consumos.fuente` (design.md:171) — the only place this
 * string lives when a real Fudo read happened. The frozen field has a SECOND
 * permitted value this module never produces: `api/liquidacion.js` overrides
 * it to `'sin-fudo-cliente-id'` for an employee with no `fudo_cliente_id` at
 * all (see `DESCUENTO_CONSUMOS_FUENTE_SIN_CLIENTE` there for the rationale).
 */
const DESCUENTO_CONSUMOS_FUENTE = 'fudo-discounts@v1';

/** Literal `descuento_consumos.reconciliacion.metodo` (design.md:181-183) — the only place this string lives. */
const DESCUENTO_CONSUMOS_RECONCILIACION_METODO = 'discount_amount';

/**
 * @param {unknown} value
 * @param {string} fieldName
 * @returns {number} integer cents
 * @throws {TypeError} si el valor esta ausente o no es un numero valido
 */
function toCents(value, fieldName) {
    // Ausente se rechaza EXPLICITO: silenciarlo a 0 aca seria la misma clase
    // de bug que AD7 prohibe en los ledgers de Fudo, del lado de la
    // liquidacion en vez del lado de la lectura — un valor/consumo/adelanto
    // perdido se convierte en pago DE MAS, nunca de menos.
    if (value === null || value === undefined) {
        throw new TypeError(`payroll-policy: ${fieldName} ausente`);
    }
    const n = Number(value);
    if (!Number.isFinite(n)) {
        throw new TypeError(`payroll-policy: ${fieldName} invalido`);
    }
    return Math.round(n * 100);
}

/** @param {unknown} value @param {string} fieldName */
function assertFecha(value, fieldName) {
    if (typeof value !== 'string' || !FECHA_RE.test(value)) {
        throw new TypeError(`payroll-policy: ${fieldName} invalido (se espera 'YYYY-MM-DD')`);
    }
}

/**
 * F3: `reconciliacionLedger` es REQUERIDO (ya no se mergea despues de armar
 * el bloque — ver `sales-ledger.js`'s `entry.descuentos.reconciliacion`).
 * @param {unknown} value
 * @returns {{comprobadas: number, omitidas: number, delta_max: number, omitidas_motivo: Array}}
 */
function assertReconciliacionLedger(value) {
    if (value == null || typeof value !== 'object') {
        throw new TypeError('payroll-policy: reconciliacionLedger ausente');
    }
    if (!Number.isFinite(value.comprobadas) || !Number.isFinite(value.omitidas) || !Number.isFinite(value.delta_max)) {
        throw new TypeError('payroll-policy: reconciliacionLedger invalido (comprobadas/omitidas/delta_max deben ser numericos)');
    }
    if (!Array.isArray(value.omitidas_motivo)) {
        throw new TypeError('payroll-policy: reconciliacionLedger.omitidas_motivo invalido (se espera un array)');
    }
    return value;
}

/**
 * @param {object} empleado
 * @returns {{ tipoPago: string, valorCents: number, pagaFeriadoDoble: boolean }}
 */
function assertEmpleado(empleado) {
    if (empleado == null || typeof empleado !== 'object') {
        throw new TypeError('payroll-policy: empleado ausente');
    }
    if (!TIPOS_PAGO_VALIDOS.includes(empleado.tipo_pago)) {
        throw new TypeError('payroll-policy: empleado.tipo_pago invalido');
    }
    if (typeof empleado.paga_feriado_doble !== 'boolean') {
        throw new TypeError('payroll-policy: empleado.paga_feriado_doble ausente o invalido');
    }
    return {
        tipoPago: empleado.tipo_pago,
        valorCents: toCents(empleado.valor, 'empleado.valor'),
        pagaFeriadoDoble: empleado.paga_feriado_doble,
    };
}

/**
 * ART calendar month ('YYYY-MM') of a real UTC instant (AD8) — shift back by
 * `ART_OFFSET_MINUTES`, read UTC calendar fields off the SHIFTED instant.
 * Mirrors `timesheet-policy.js`'s private (unexported) `artDateKey`;
 * reimplemented here and in this module's twin.
 */
function mesArt(saleCreatedAt) {
    // Rechazo explicito (mismo motivo que `toMs`, timesheet-policy.js:61-81):
    // sin esto, un timestamp ausente/invalido inventa un mes fantasma.
    if (saleCreatedAt == null || typeof saleCreatedAt !== 'string') {
        throw new TypeError('payroll-policy: descuentoItems[].saleCreatedAt ausente o invalido');
    }
    const ms = new Date(saleCreatedAt).getTime();
    if (Number.isNaN(ms)) {
        throw new TypeError('payroll-policy: descuentoItems[].saleCreatedAt invalido');
    }
    const shifted = new Date(ms - ART_OFFSET_MINUTES * 60000);
    return `${shifted.getUTCFullYear()}-${String(shifted.getUTCMonth() + 1).padStart(2, '0')}`;
}

/** Every 'YYYY-MM' touched by [desde, hasta], inclusive. String-slicing arithmetic — never `new Date(...)` (same posture as `api/liquidacion.js`'s `ultimoDiaDelMesArt`). */
function monthsInRange(desde, hasta) {
    const meses = [];
    let anio = Number(desde.slice(0, 4));
    let mes = Number(desde.slice(5, 7));
    const anioFin = Number(hasta.slice(0, 4));
    const mesFin = Number(hasta.slice(5, 7));
    while (anio < anioFin || (anio === anioFin && mes <= mesFin)) {
        meses.push(`${anio}-${String(mes).padStart(2, '0')}`);
        mes += 1;
        if (mes > 12) {
            mes = 1;
            anio += 1;
        }
    }
    return meses;
}

/**
 * Pure cap-accumulation function (`employee-discount-cap` spec; design.md
 * "Payroll policy" :120-155, AD7-AD11). Called once per employee from
 * `api/liquidacion.js`'s phase-2 loop — NEVER from `computeLiquidacion`
 * below, which does not call it; the caller threads `excedenteTotal` into
 * `computeLiquidacion` as `descuentoExcedenteTotal`. `bloque` freezes
 * verbatim into `detalle.descuento_consumos` on `confirm` (append-only).
 *
 * Per ART month M touched by [desde, hasta] OR by any item's own ART month
 * (AD8 edge: an item can bucket into an ADJACENT period's month near the
 * 01:00 ART cut — still gets its own `por_mes` entry, never dropped):
 *   descuento_periodo(M) = Σ item.amount con mesArt(item.saleCreatedAt) === M
 *   descuento_previo(M)  = Σ prev.detalle.descuento_consumos.por_mes[M].descuento_imputado
 *                           (solo priors que TIENEN un por_mes[M] — AD10)
 *   disponible(M)         = max(0, topeMensual − descuento_previo(M))
 *   descuento_imputado(M) = min(descuento_periodo(M), disponible(M))
 *   excedente(M)          = max(0, descuento_periodo(M) − disponible(M))
 * excedenteTotal = Σ excedente(M)
 *
 * Un `prev` sin el bloque nunca llega aca (el caller ya 409s, AD9/AD10); uno
 * CON el bloque pero sin el mes M aporta un 0 legitimo y NO entra en
 * `liquidaciones_previas` de ese mes.
 *
 * Money: mismo cents discipline que {@link toCents} — sumas en cents
 * enteros, division por 100 solo al armar el bloque congelado.
 *
 * @param {object} params
 * @param {Array<{saleId: string, discountId: string, amount: number, percentage: number|null, saleCreatedAt: string}>} params.descuentoItems
 * @param {Array<{id: string, detalle: object}>} params.liquidacionesPrevias frozen snapshots ONLY (AD9)
 * @param {number|string} params.topeMensual ausente/NaN/negativo THROWS (AD10/AD11)
 * @param {string} params.desde 'YYYY-MM-DD' inclusive
 * @param {string} params.hasta 'YYYY-MM-DD' inclusive
 * @param {{comprobadas: number, omitidas: number, delta_max: number, omitidas_motivo: Array}} params.reconciliacionLedger REQUERIDO — `sales-ledger.js`'s `entry.descuentos.reconciliacion`; ausente/malformado THROWS (F3 bounded correction, review-88359f0b24a9b2a2)
 * @returns {{ bloque: object, excedenteTotal: number }}
 */
export function computeDescuentoCap({ descuentoItems, liquidacionesPrevias, topeMensual, desde, hasta, reconciliacionLedger } = {}) {
    if (!Array.isArray(descuentoItems)) {
        throw new TypeError('payroll-policy: descuentoItems ausente (se espera un array, incluso vacio)');
    }
    if (!Array.isArray(liquidacionesPrevias)) {
        throw new TypeError('payroll-policy: liquidacionesPrevias ausente (se espera un array, incluso vacio)');
    }
    const topeMensualCents = toCents(topeMensual, 'topeMensual');
    if (topeMensualCents < 0) {
        throw new TypeError('payroll-policy: topeMensual no puede ser negativo');
    }
    assertFecha(desde, 'desde');
    assertFecha(hasta, 'hasta');
    if (hasta < desde) {
        throw new TypeError('payroll-policy: hasta no puede ser anterior a desde');
    }
    assertReconciliacionLedger(reconciliacionLedger);

    const items = descuentoItems.map((item) => ({
        saleId: item.saleId,
        discountId: item.discountId,
        amount: item.amount,
        percentage: item.percentage,
        saleCreatedAt: item.saleCreatedAt,
        mes: mesArt(item.saleCreatedAt),
    }));

    // Meses a liquidar: el rango SIEMPRE aporta los suyos (aunque no haya
    // items ese mes); union con cualquier mes al que un item haya
    // bucketeado (AD8) — nunca solo uno de los dos conjuntos.
    const meses = new Set(monthsInRange(desde, hasta));
    for (const item of items) meses.add(item.mes);

    const periodoCentsPorMes = new Map();
    for (const item of items) {
        const cents = toCents(item.amount, 'descuentoItems[].amount');
        // F2: negativo se rechaza aparte (mismo patron que topeMensual arriba).
        if (cents < 0) {
            throw new TypeError('payroll-policy: descuentoItems[].amount no puede ser negativo');
        }
        periodoCentsPorMes.set(item.mes, (periodoCentsPorMes.get(item.mes) ?? 0) + cents);
    }

    const porMes = {};
    let excedenteTotalCents = 0;
    for (const mes of [...meses].sort()) {
        const periodoCents = periodoCentsPorMes.get(mes) ?? 0;

        let previoCents = 0;
        const liquidacionesPreviasMes = [];
        for (const prev of liquidacionesPrevias) {
            const entradaPrevia = prev?.detalle?.descuento_consumos?.por_mes?.[mes];
            if (entradaPrevia === undefined) continue; // presencia, no ausencia (AD10)
            const impCents = toCents(entradaPrevia.descuento_imputado, `liquidacionesPrevias.detalle.descuento_consumos.por_mes[${mes}].descuento_imputado`);
            // F2: un prior negativo inflaria `disponible` mas alla del tope (append-only) — nunca clamp.
            if (impCents < 0) {
                throw new TypeError('payroll-policy: liquidacionesPrevias.detalle.descuento_consumos.por_mes[].descuento_imputado no puede ser negativo');
            }
            previoCents += impCents;
            liquidacionesPreviasMes.push(prev.id);
        }

        const disponibleCents = Math.max(0, topeMensualCents - previoCents);
        const imputadoCents = Math.min(periodoCents, disponibleCents);
        const excedenteCents = Math.max(0, periodoCents - disponibleCents);
        excedenteTotalCents += excedenteCents;

        porMes[mes] = {
            descuento_periodo: periodoCents / 100,
            descuento_previo: previoCents / 100,
            disponible: disponibleCents / 100,
            descuento_imputado: imputadoCents / 100,
            excedente: excedenteCents / 100,
            liquidaciones_previas: liquidacionesPreviasMes,
        };
    }

    return {
        bloque: {
            fuente: DESCUENTO_CONSUMOS_FUENTE,
            tope_mensual: topeMensualCents / 100,
            excedente_total: excedenteTotalCents / 100,
            por_mes: porMes,
            items,
            // F3: comprobadas/omitidas/delta_max/omitidas_motivo vienen del
            // reconciliacionLedger REQUERIDO (sin merge posterior). `metodo` es propio de esta funcion.
            reconciliacion: {
                metodo: DESCUENTO_CONSUMOS_RECONCILIACION_METODO,
                comprobadas: reconciliacionLedger.comprobadas,
                omitidas: reconciliacionLedger.omitidas,
                delta_max: reconciliacionLedger.delta_max,
                omitidas_motivo: reconciliacionLedger.omitidas_motivo,
            },
        },
        excedenteTotal: excedenteTotalCents / 100,
    };
}

/**
 * `hora` PATH ONLY. Aggregates every fichada's date-buckets (via
 * {@link splitMinutesByDate}) into one entry per ART date touched,
 * restricted to `[desde, hasta]`. Multiple fichadas landing on the same
 * date are summed. A shift crossing ART midnight contributes to BOTH dates
 * it touches — an hourly employee is paid for exact minutes worked on each
 * calendar date, so the holiday multiplier must follow the minutes, not the
 * whole shift. This is unchanged by the `dia`-path business-rule correction
 * below (see {@link diasPorEntrada}) — the two pay types deliberately
 * attribute a midnight-crossing shift differently.
 *
 * @param {Array<object>} fichadas
 * @param {Set<string>} feriados
 * @param {string} desde
 * @param {string} hasta
 * @returns {Array<{ fecha: string, minutos: number, feriado: boolean }>} sorted ascending by fecha
 */
function diasEnRango(fichadas, feriados, desde, hasta) {
    const porFecha = new Map();

    for (const fichada of fichadas) {
        for (const bucket of splitMinutesByDate(fichada)) {
            if (bucket.fecha < desde || bucket.fecha > hasta) continue;
            porFecha.set(bucket.fecha, (porFecha.get(bucket.fecha) ?? 0) + bucket.minutos);
        }
    }

    return [...porFecha.entries()]
        .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
        .map(([fecha, minutos]) => ({ fecha, minutos, feriado: feriados.has(fecha) }));
}

/**
 * `dia` PATH ONLY. Attributes each fichada ENTIRELY to the ART calendar
 * date of its `entrada_at` — one shift is one paid day, on the date it
 * started, restricted to `[desde, hasta]` (membership is checked against
 * the entrada date, not any date the shift crosses into).
 *
 * BUSINESS RULE (owner decision, not an implementation detail — corrects an
 * earlier judgment call that counted every ART date a shift touched): a
 * closing shift that crosses ART midnight (routine in food service, not an
 * edge case — `ART_OFFSET_MINUTES = 180` puts the cut at local midnight)
 * previously paid TWO days for ONE shift, a systematic ~100% overpay for
 * every `dia`-type employee's normal closing shift. The fix: the shift's
 * FULL duration (even the portion past midnight) counts toward the entrada
 * date only, and that same entrada date ALSO decides the holiday flag for
 * the whole shift — a shift starting on a normal day and finishing on a
 * `feriados` date is NOT doubled; a shift starting on a `feriados` date
 * doubles its full pay even if it crosses into a normal day.
 *
 * Multiple fichadas sharing the same entrada date still count as ONE worked
 * day (their minutes are summed for the audit trail in `detalle.dias`, but
 * `dias_normales`/`dias_feriado` only count the distinct date once).
 *
 * @param {Array<object>} fichadas
 * @param {Set<string>} feriados
 * @param {string} desde
 * @param {string} hasta
 * @returns {Array<{ fecha: string, minutos: number, feriado: boolean }>} sorted ascending by fecha
 */
function diasPorEntrada(fichadas, feriados, desde, hasta) {
    const porFecha = new Map();

    for (const fichada of fichadas) {
        // buckets[0].fecha is ALWAYS the ART date of entrada_at:
        // splitMinutesByDate's cursor starts at entrada_at, so the first
        // bucket it ever produces is entrada's own date — reused here
        // instead of re-deriving ART date math a second time.
        const buckets = splitMinutesByDate(fichada);
        const fechaEntrada = buckets[0].fecha;
        if (fechaEntrada < desde || fechaEntrada > hasta) continue;

        const minutosTotal = buckets.reduce((sum, b) => sum + b.minutos, 0);
        porFecha.set(fechaEntrada, (porFecha.get(fechaEntrada) ?? 0) + minutosTotal);
    }

    return [...porFecha.entries()]
        .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
        .map(([fecha, minutos]) => ({ fecha, minutos, feriado: feriados.has(fecha) }));
}

/**
 * Full settlement engine (payroll-settlement spec). Computes bruto/neto for
 * ONE employee over `[desde, hasta]`. Does NOT call `detectAnomalies`
 * itself — the caller must gate on that first (Requirement: "Settlement
 * Blocked While Anomalies Are Unresolved").
 *
 * `tipo_pago = 'hora'`: `bruto = round(Σ(minutos_fecha × (feriado && doble ? MULTIPLICADOR_FERIADO : 1)) × valor / 60)`,
 * minutes split per ART calendar date via {@link splitMinutesByDate} — a shift crossing ART
 * midnight is paid per exact minute on each date it touches, holiday multiplier included.
 *
 * `tipo_pago = 'dia'`: `bruto = round(Σ dias × valor × (feriado && doble ? MULTIPLICADOR_FERIADO : 1))`,
 * where EACH FICHADA COUNTS AS EXACTLY ONE DAY, attributed ENTIRELY to the ART date of its
 * `entrada_at` (OWNER BUSINESS RULE, not an implementation detail — see
 * {@link diasPorEntrada}'s JSDoc for the full rationale). A shift starting at 20:00 and
 * ending at 02:00 the next ART day still pays ONE day, not two — this was a real bug
 * (systematic ~100% overpay on every `dia`-employee's normal closing shift, since a
 * midnight-crossing close is the routine case in food service, not an edge case) that
 * attributed the shift to BOTH dates it touched. The entrada date ALSO decides the holiday
 * flag for the ENTIRE shift: starting on a normal day and finishing on a `feriados` date does
 * NOT double the pay; starting on a `feriados` date doubles the FULL shift even if it crosses
 * into a normal day. This rule applies ONLY to `tipo_pago = 'dia'` — the `hora` path above is
 * unaffected and keeps splitting minutes (and the holiday flag) per calendar date.
 *
 * No minimums or caps in either path — a shift over `ANOMALY_MAX_SHIFT_MINUTES` is an anomaly
 * (gated upstream), never truncated here.
 *
 * `tipo_pago = 'fijo'`: `bruto = valor`, TAKEN IN FULL for whatever period is settled — NO
 * proration by days or hours in range (AD2, owner decision LOCKED). This is the exact opposite of
 * `hora`/`dia`, where zero fichadas means `bruto = 0`: a `fijo` employee with zero fichadas still
 * settles to `bruto = valor`. `detalle.dias` is still built (via {@link diasPorEntrada}, same as
 * `dia`) so attendance stays auditable, but it is INFORMATIONAL ONLY for `fijo` — it never affects
 * `bruto`. The holiday multiplier is NEVER applied to `fijo` regardless of `paga_feriado_doble`
 * (AD3): `detalle.multiplicador_aplicado` is `1` for `fijo` even when the DB column
 * `multiplicador_feriado` keeps stamping the policy constant `MULTIPLICADOR_FERIADO = 2` (that
 * column means "the policy constant in force at freeze time", not "the effective multiplier" — see
 * AD3's rationale). Consequence the owner explicitly accepted: settling the same `fijo` employee
 * twice for the same/overlapping period pays `valor` in full BOTH times — this function has no
 * cross-call memory, and preventing a double-pay of the *same* period is the overlap guard's job
 * (`payroll-settlement` spec: "No Overlapping Settlement Periods"), not proration here.
 *
 * @param {object} params
 * @param {{ tipo_pago: 'hora'|'dia'|'fijo', valor: number|string, paga_feriado_doble: boolean }} params.empleado
 * @param {Array<object>} params.fichadas fichadas del rango, no anuladas
 * @param {Set<string>} params.feriados fechas ART ('YYYY-MM-DD') feriado
 * @param {number|string} params.consumosTotal
 * @param {number|string} params.adelantosTotal
 * @param {number|string} params.otrosDescuentosTotal REQUERIDO (spec liquidacion-otros-descuentos,
 *   B3) — suma de los otros descuentos activos del periodo. Ausente, no numerico o negativo
 *   THROWS, mismo criterio SILENT ZERO que `adelantosTotal`.
 * @param {number|string} params.descuentoExcedenteTotal REQUERIDO (D3b) — el excedente del tope
 *   de descuentos (`computeDescuentoCap`'s `excedenteTotal`) que se suma a `consumos`. Ausente,
 *   no numerico o negativo THROWS — nunca default a 0 (un 0 defaulteado aca perdonaria en
 *   silencio cualquier excedente real, la misma clase SILENT ZERO que el resto del modulo).
 * @param {string} params.desde 'YYYY-MM-DD' inclusive
 * @param {string} params.hasta 'YYYY-MM-DD' inclusive
 * @returns {{ bruto: number, consumos: number, adelantos: number, otros_descuentos: number, neto: number, detalle: object }}
 */
export function computeLiquidacion({ empleado, fichadas, feriados, consumosTotal, adelantosTotal, otrosDescuentosTotal, descuentoExcedenteTotal, desde, hasta } = {}) {
    const { tipoPago, valorCents, pagaFeriadoDoble } = assertEmpleado(empleado);

    if (!Array.isArray(fichadas)) {
        throw new TypeError('payroll-policy: fichadas ausente (se espera un array, incluso vacio)');
    }
    if (!(feriados instanceof Set)) {
        throw new TypeError('payroll-policy: feriados ausente (se espera un Set, incluso vacio)');
    }
    assertFecha(desde, 'desde');
    assertFecha(hasta, 'hasta');
    if (hasta < desde) {
        throw new TypeError('payroll-policy: hasta no puede ser anterior a desde');
    }

    const consumosCents = toCents(consumosTotal, 'consumosTotal');
    const adelantosCents = toCents(adelantosTotal, 'adelantosTotal');
    const otrosDescuentosCents = toCents(otrosDescuentosTotal, 'otrosDescuentosTotal');
    // Una suma de montos > 0 nunca es negativa: rechazarla en vez de dejar
    // que SUME al neto en silencio (mismo criterio que el excedente de abajo).
    if (otrosDescuentosCents < 0) {
        throw new TypeError('payroll-policy: otrosDescuentosTotal no puede ser negativo');
    }
    const descuentoExcedenteCents = toCents(descuentoExcedenteTotal, 'descuentoExcedenteTotal');
    // F2 (mismo patron que el guard de topeMensual/amount en computeDescuentoCap): un excedente
    // negativo nunca puede ocurrir legitimamente (viene de un max(0, ...)) — rechazarlo aca en vez
    // de dejarlo restar de `consumos` en silencio.
    if (descuentoExcedenteCents < 0) {
        throw new TypeError('payroll-policy: descuentoExcedenteTotal no puede ser negativo');
    }

    // El path decide COMO se atribuyen los dias/minutos: hora parte por fecha
    // (diasEnRango); dia y fijo atribuyen el turno ENTERO a la fecha de
    // entrada (diasPorEntrada) — regla de negocio del owner, ver el JSDoc de
    // arriba. Para fijo, `dias` es SOLO rastro de auditoria (AD2): nunca
    // entra en el calculo de bruto de abajo.
    const dias = tipoPago === 'hora' ? diasEnRango(fichadas, feriados, desde, hasta) : diasPorEntrada(fichadas, feriados, desde, hasta);
    const multiplicadorEfectivo = pagaFeriadoDoble ? MULTIPLICADOR_FERIADO : 1;
    // AD3: un sueldo fijo NUNCA se duplica por feriado, se ignore o no
    // `paga_feriado_doble` en el empleado — el efectivo es siempre 1. La
    // columna `multiplicador_feriado` (constante de politica) sigue
    // estampando 2 igual: significa "la constante vigente al congelar", no
    // "el multiplicador que se aplico" (ver JSDoc de arriba).
    const multiplicadorAplicado = tipoPago === 'fijo' ? 1 : multiplicadorEfectivo;

    const minutosNormales = dias.filter((d) => !d.feriado).reduce((sum, d) => sum + d.minutos, 0);
    const minutosFeriado = dias.filter((d) => d.feriado).reduce((sum, d) => sum + d.minutos, 0);
    const diasNormales = dias.filter((d) => !d.feriado && d.minutos > 0).length;
    const diasFeriado = dias.filter((d) => d.feriado && d.minutos > 0).length;

    // UN solo redondeo, aca, sobre el bruto completo del periodo (AD8) — nunca
    // por turno ni por dia ni por bucket de fecha. Los minutos que llegan de
    // splitMinutesByDate son exactos (sin redondear); la unica perdida de
    // precision de toda la cuenta ocurre UNA vez, aca. `fijo` no tiene
    // computo alguno: `bruto = valor`, tomado entero para el periodo
    // liquidado, sin prorratear por dias/horas en rango (AD2, owner decision
    // LOCKED) — un empleado fijo con CERO fichadas igual liquida `valor`,
    // exactamente lo opuesto de hora/dia (donde cero fichadas es bruto 0).
    const brutoCentsFloat =
        tipoPago === 'fijo'
            ? valorCents
            : tipoPago === 'hora'
              ? ((minutosNormales + minutosFeriado * multiplicadorEfectivo) * valorCents) / 60
              : (diasNormales + diasFeriado * multiplicadorEfectivo) * valorCents;
    const brutoCents = Math.round(brutoCentsFloat);
    // consumos = consumosTotal + descuentoExcedenteTotal (design.md :152, Q6
    // Parte 2): el excedente del tope se recupera del sueldo sin importar
    // que medio de pago lo genero — se suma a `consumos`, nunca aparte.
    const consumosConExcedenteCents = consumosCents + descuentoExcedenteCents;
    const netoCents = brutoCents - consumosConExcedenteCents - adelantosCents - otrosDescuentosCents;

    return {
        bruto: brutoCents / 100,
        consumos: consumosConExcedenteCents / 100,
        adelantos: adelantosCents / 100,
        otros_descuentos: otrosDescuentosCents / 100,
        neto: netoCents / 100,
        detalle: {
            // AD1: UNA sola forma para las tres modalidades — nunca una union
            // que el que lee tenga que sniffear. Estas tres claves estan
            // SIEMPRE presentes en las tres modalidades; `valor_fijo` es
            // `null` fuera de `fijo`, nunca omitida.
            policy_version: PAYROLL_POLICY_VERSION,
            multiplicador_feriado: MULTIPLICADOR_FERIADO,
            multiplicador_aplicado: multiplicadorAplicado,
            modo: tipoPago,
            valor_fijo: tipoPago === 'fijo' ? valorCents / 100 : null,
            dias,
            minutos_normales: minutosNormales,
            minutos_feriado: minutosFeriado,
            dias_normales: diasNormales,
            dias_feriado: diasFeriado,
            // design.md :168 — crudo, ANTES del ajuste por excedente (para
            // que `descuento_consumos.excedente_total` sea auditable contra
            // `consumos` sin tener que restar a mano).
            consumos_fudo: consumosCents / 100,
            // Spec liquidacion-otros-descuentos (Design fork 2): el total
            // congelado vive en `detalle`, no en una columna de liquidaciones.
            otros_descuentos: otrosDescuentosCents / 100,
        },
    };
}

/**
 * L6/D1 (owner decisions 2026-09-27) — the calendar month a settlement
 * BELONGS to: the month of its `periodo_desde` (`YYYY-MM`), so a week
 * crossing months belongs to its FIRST month. The ONE rule behind the CSV
 * export's month filter (`listLiquidaciones({ inicioDesde, inicioHasta })`
 * selects by `periodo_desde` inside the month) and the fixed-salary gate
 * below ({@link fijoYaLiquidadoEnMes}). String slicing of an ART calendar
 * date, never `Date`.
 * @param {{ periodo_desde: string }} liquidacion
 * @returns {string} `YYYY-MM`
 */
export function mesDeLiquidacion(liquidacion) {
    return String(liquidacion.periodo_desde).slice(0, 7);
}

/**
 * L10/D1 (owner decisions 2026-09-27) — a fixed salary (`tipo_pago = 'fijo'`)
 * is settled ONCE per calendar month: `bruto = valor` in every settlement
 * (see {@link computeLiquidacion}), so a second fixed-salary settlement of
 * the same month would pay the monthly salary twice. The FIRST settlement of
 * a month is allowed even when its range is partial.
 *
 * A settlement belongs to the month it STARTS in ({@link mesDeLiquidacion},
 * same rule as the CSV export): the new period `[desde, hasta]` is refused
 * when a prior CONFIRMED fixed-salary settlement of the employee starts in
 * the same month as `desde`. Only prior rows with `detalle.modo === 'fijo'`
 * count — an `hora`/`dia` settlement earlier in the month (the employee
 * switched to `fijo` mid-month) and a policy @1 row without `modo` never
 * block. An `anulada` row never counts; a row with no `estado` is taken as
 * confirmed. Overlapping periods are not this helper's job: the server's
 * `periodo_solapado` check (phase 1c) refuses them first.
 *
 * Returns the first such settlement (ordered by `periodo_desde`, so the
 * answer is deterministic) with the month (`YYYY-MM`), or `null` — ALWAYS
 * `null` for any `tipoPago` other than `'fijo'`. The caller passes the rows
 * of ONE employee.
 *
 * PARITY DUPLICATE: `api/liquidacion.js` refuses `confirm` with it (409
 * `fijo_ya_liquidado_mes`); the Liquidación tablero uses the client copy to
 * disable the row and keep it out of the bulk batch.
 * @param {{ tipoPago: string|null|undefined, desde: string, hasta: string, liquidaciones: Array<{ id: string, periodo_desde: string, periodo_hasta: string, estado?: string, detalle?: { modo?: string }|null }>|null|undefined }} params
 * @returns {{ mes: string, liquidacion: object }|null}
 */
export function fijoYaLiquidadoEnMes({ tipoPago, desde, liquidaciones } = {}) {
    if (tipoPago !== 'fijo') return null;
    const mes = mesDeLiquidacion({ periodo_desde: desde });
    const liquidacion = (liquidaciones ?? [])
        .filter((l) => l && l.estado !== 'anulada' && l.detalle?.modo === 'fijo' && mesDeLiquidacion(l) === mes)
        .sort((a, b) => String(a.periodo_desde).localeCompare(String(b.periodo_desde)))[0];
    return liquidacion ? { mes, liquidacion } : null;
}
